<template>
  <div class="e-circular ecircular-detail">
    <TMSHeader
      :title="title"
      :buttons="headerButtons"
      :dotOption="dotOption"
      :status="currentECircular.status"
      :needGoback="true"
    />
    <div class="main-content">
      <label class="title">
        {{ `${$t('modified')} ${$t('history')}` }}
      </label>
      <div class="non-sticky-table">
        <table class="non-sticky-table-header">
          <thead>
            <tr class="sort-tr">
              <th
                v-for="(value, index) in historyTabelHead"
                :key="index"
              >
                {{ value }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in currentHistories"
              :key="index"
            >
              <td class="ecircular-detail__th-summary">
                <router-link
                  v-if="showSentResult(item)"
                  :to="{ name: 'eCircularHistorySummary', params: { id: ecircularId, hid: item.id } }"
                >
                  {{ `${sentCount(item)}/${item.sentTotal} ${$t('action.sent')}` }}
                </router-link>
                <span v-else>--</span>
              </td>
              <td class="ecircular-detail__th-updator">
                {{ item.updatorName }}
              </td>
              <td class="ecircular-detail__th-status">
                {{ item.status | customStatusLabel }}
              </td>
              <td class="ecircular-detail__th-date">
                {{ item.statusDate }}
              </td>
              <td>
                <div class="ecircular-detail__th-remarks">
                  <template v-if="!editingRemarks[item.id]">
                    <pre>{{ item.remarks | isEmptyValue }}</pre>
                    <span
                      v-if="canEditRemark"
                      class="fa fa-pencil icon pointer"
                      @click="enableEditRemarks(item.id)"
                    />
                  </template>
                  <template v-else>
                    <textarea
                      v-model="item.remarks"
                      v-autosize
                      v-checkVisibility="{refresh: true}"
                      class="autosize"
                      autofocus
                      @focusout="updateRemarks(item)"
                    />
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <label class="title">{{ $t('eCircular.detail') }}</label>
      <CreateECircular
        ref="eCircular-edit"
        :showHeader="false"
        :canEdit="canEdit"
      />
    </div>
    <PreviewECircular
      v-if="showAtResend"
      ref="resendPreviewECircular"
      :previewType="PREVIEW_TYPES.RESEND"
    />

    <Modal
      ref="ecircular-info-modal"
      :modalId="infoModalId"
      modalType="small-modal"
      class="ecircular-modal"
      :showCloseBtn="false"
    >
      <p
        slot="modal-header"
        class="modal-title"
      >
        {{ $t(`eCircular.chooseRecipients.${currentECircular.type}`) }}
      </p>
      <p
        slot="content"
        class="font-bold"
      >
        {{ $t('eCircular.setZoneMsg') }}
      </p>
      <div slot="btns">
        <button
          class="btn--orange"
          @click="closeModal('ecircular-info-modal')"
        >
          {{ $t('action.ok') }}
        </button>
      </div>
    </Modal>

    <Modal
      ref="ecircular-edit-modal"
      :modalId="editModalId"
      modalType="small-modal"
      :showCloseBtn="false"
      class="ecircular-modal"
    >
      <p
        slot="content"
        class="font-bold"
      >
        {{ $t('eCircular.editECricularHint') }}
      </p>
      <div slot="btns">
        <button
          class="btn--orange"
          @click="closeModal('ecircular-edit-modal')"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          class="btn--darkblue"
          @click="confirmEdit()"
        >
          {{ $t('action.yes') }}
        </button>
      </div>
    </Modal>

    <CancelECircular ref="ecircular-cancel-modal" />
  </div>
</template>

<script>
import { cloneDeep, get } from 'lodash'
import { mapGetters, mapActions } from 'vuex'

import Modal from '@/controls/Modal'
import TMSHeader from '@/controls/TMSHeader'
import CancelECircular from './CancelECircular'
import CreateECircular from './CreateECircular'
import PreviewECircular from './PreviewECircular'

import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import * as ecircularAPI from '@/api/ecircular/request'
import store from '@/store'
import { utils as formUtils } from '@/plugin/InputChange'
import { aclHelper, aclPermsType, tmsPermsTypes } from '@/utils/aclHelper'
import { E_CIRCULAR_STATUS, E_CIRCULAR_WORKING_STATUS, EMAIL_SENT_RESULTS, PREVIEW_TYPES } from './consts'

export default {
  name: 'ECircularDetail',
  components: {
    CancelECircular,
    CreateECircular,
    TMSHeader,
    PreviewECircular,
    Modal,
  },
  async beforeRouteEnter (to, from, next) {
    const id = to.params.id
    // load current ecircular before the page render to make sure child component can use current ecircular
    await store['_actions']['ecircular/loadECircular'][0](id)
    next()
  },
  data () {
    const infoModalId = `ecircular-info-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`
    const editModalId = `ecircular-edit-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`
    return {
      canEdit: false,
      canEditRemarks: false,
      editModalId,
      editingRemarks: {},
      ecircularId: this.$route.params.id,
      infoModalId,
      originECircular: null,
      E_CIRCULAR_STATUS,
      PREVIEW_TYPES,
    }
  },
  computed: {
    ...mapGetters('ecircular', ['currentECircular']),
    ...mapGetters('user', ['buildings', 'currentUser']),
    showAtResend () {
      const avaliableStatus = [E_CIRCULAR_STATUS.SENT, E_CIRCULAR_STATUS.RESENT]
      return avaliableStatus.includes(this.currentECircular.status)
    },
    title () {
      return `${this.currentECircular.subject || '--'}`
    },
    canUpdateVendorEcircular () {
      return aclHelper.getAccess(aclPermsType.vendorECircular.createUpdateSelectSendCancel)
    },
    canEditRemark () {
      return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.createUpdate) || aclHelper.getAccess(tmsPermsTypes.tenantEcircular.send) || this.canUpdateVendorEcircular
    },
    canCreateUpdate () {
      return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.createUpdate) || this.canUpdateVendorEcircular
    },
    canCancel () {
      return (aclHelper.getAccess(tmsPermsTypes.tenantEcircular.cancel) || this.canUpdateVendorEcircular) && Object.values(E_CIRCULAR_WORKING_STATUS).includes(this.currentECircular.status)
    },
    canResend () {
      return (this.canTenantResend || this.canUpdateVendorEcircular) && [E_CIRCULAR_STATUS.SENT, E_CIRCULAR_STATUS.RESENT].includes(this.currentECircular.status)
    },
    canTenantResend () {
      return this.hasBuildingAccess && aclHelper.getAccess(tmsPermsTypes.tenantEcircular.send)
    },
    canSend () {
      return this.canTenantSend || this.canVendorSend
    },
    canTenantSend () {
      return this.hasBuildingAccess &&
        E_CIRCULAR_STATUS.SUBMITTED === this.currentECircular.status &&
        aclHelper.getAccess(tmsPermsTypes.tenantEcircular.send)
    },
    canVendorSend () {
      return E_CIRCULAR_STATUS.SUBMITTED === this.currentECircular.status &&
        this.canUpdateVendorEcircular
    },
    hasBuildingAccess () {
      const hasBuildingAccess = this.buildings.some(
        building => this.currentECircular.buildings.indexOf(building) > -1,
      )
      return hasBuildingAccess
    },
    headerButtons () {
      const editButton = []

      if (this.canSend && !this.canEdit &&
        Object.keys(E_CIRCULAR_WORKING_STATUS).includes(this.currentECircular.status)) {
        editButton.push({
          title: i18n.t('action.edit'),
          cls: 'btn--orange',
          func: this.enableEdit,
        })
      }

      if (this.canEdit) {
        editButton.push({
          title: i18n.t('action.cancel'),
          cls: 'btn--darkblue',
          func: this.cancelEdit,
        }, {
          title: i18n.t('action.save'),
          cls: 'btn--orange',
          func: this.save,
        })
      }

      const addRecipientsButton = this.canSend && !this.canEdit ? [{
        title: i18n.t('action.addRecipients'),
        cls: 'btn--orange',
        func: this.addRecipients,
      }] : []
      const resendButton = this.canResend ? [{
        title: i18n.t('action.resend'),
        cls: 'btn--orange',
        func: this.openResendPreview,
      }] : []
      return [...editButton, ...addRecipientsButton, ...resendButton]
    },
    historyTabelHead () {
      return [
        i18n.t('eCircular.recipientSummary').toUpperCase(),
        i18n.t('actionBy').toUpperCase(),
        i18n.t('status.name').toUpperCase(),
        i18n.t('statusDate').toUpperCase(),
        i18n.t('common.remarks').toUpperCase(),
      ]
    },
    dotOption () {
      let options = []
      if (this.canCreateUpdate) {
        options.push({
          title: i18n.t('action.duplicate'),
          operate: this.duplicateECircular,
        })
      }
      if (this.canCancel) {
        options.push({
          title: i18n.t('action.cancel'),
          operate: this.openCancelModal,
        })
      }
      return options
    },
    currentHistories () {
      const currentECircular = cloneDeep(this.currentECircular)
      if (!currentECircular.histories) return []
      const result = currentECircular.histories.map(item => {
        item.statusDate = dateUtil.formatDate(item.updatedAt, true)
        return item
      })
      return result
    },
  },
  mounted () {
    this.$refs['eCircular-edit'].buildingDisabled = false
  },
  methods: {
    ...mapActions('ecircular', [
      'loadECircular',
      'createECircular',
      'updateECircular',
    ]),
    showSentResult (item = {}) {
      return E_CIRCULAR_STATUS.CANCELLED !== item.status && !Object.values(E_CIRCULAR_WORKING_STATUS).includes(item.status)
    },
    sentCount (item = {}) {
      let count = '--'
      if (E_CIRCULAR_STATUS.SENDING !== item.status && !this.hasPendingRecipientStatus(item)) {
        count = item.sentSuccess
      }
      return count
    },
    hasPendingRecipientStatus (item = {}) {
      if (get(item, 'recipientTrees.length')) {
        const tenancies = item.recipientTrees
        if (get(tenancies, 'recipients.length') &&
          tenancies.recipients.some(recipient => recipient.status === EMAIL_SENT_RESULTS.PENDING)) {
          return true
        }
      }
      if (get(item, 'standaloneRecipients.length')) {
        if (item.standaloneRecipients.some(recipient => recipient.status === EMAIL_SENT_RESULTS.PENDING)) {
          return true
        }
      }
      return false
    },
    enableEdit () {
      if (this.currentECircular.status === E_CIRCULAR_STATUS.SCHEDULED) {
        this.openEditHintModal()
      } else {
        this.originECircular = cloneDeep(this.currentECircular)
        this.canEdit = true
      }
    },
    async confirmEdit () {
      await this.updateECircular({
        id: this.ecircularId,
        name: 'scheduleDate',
        value: null,
      })
      await ecircularAPI.updateECircularStatus(this.ecircularId, E_CIRCULAR_STATUS.SUBMITTED)
      await this.loadECircular(this.ecircularId)
      this.originECircular = cloneDeep(this.currentECircular)
      this.canEdit = true
      this.closeModal('ecircular-edit-modal')
    },
    async cancelEdit () {
      await this.loadECircular(this.ecircularId)
      this.$refs['eCircular-edit'].resetData()
      this.canEdit = false
      formUtils.resetDirty()
    },
    async duplicateECircular () {
      await this.createECircular({id: this.currentECircular.id})
    },
    async save () {
      const isValid = this.$refs['eCircular-edit'].validation()
      if (isValid) {
        await this.$refs['eCircular-edit'].save()
        this.originECircular = cloneDeep(this.currentECircular)
        this.canEdit = false
      }
    },
    async addRecipients () {
      const isOverSized = await this.$refs['eCircular-edit'].checkAttachmentLimits(this.currentECircular.files)
      if (!isOverSized) {
        this.$router.push({
          name: 'addRecipients',
          params: {
            id: this.$route.params.id,
          },
        })
      }
    },
    openResendPreview () {
      this.$refs.resendPreviewECircular.open()
    },
    openEditHintModal () {
      this.$refs['ecircular-edit-modal'].open()
    },
    closeModal (refName) {
      this.$refs[refName].close()
    },
    updateUserAddRecipients (who) {
      this.userAddRecipients = who
    },
    openCancelModal () {
      this.$refs['ecircular-cancel-modal'].open()
    },
    enableEditRemarks (id) {
      this.$set(this.editingRemarks, id, true)
    },
    async updateRemarks (history) {
      await ecircularAPI.updateHistory(history.id, { remarks: history.remarks })
      this.editingRemarks[history.id] = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/common';
@import '~styles/commonTable';
</style>

<style lang='scss'>
@import '~styles/e-circular/styles';
.ecircular-detail {
  .tms-header {
    padding-left: 15px;
    padding-right: 15px;
  }
  .main-content {
    .create-ecircular {
      &-form {
        box-shadow: none;
        margin: 0;
        padding: 0;
      }
      & .attachments.input-row {
        margin: 0;
        padding: 0;
      }
    }
    label.title {
      font-size: 16px;
      margin-bottom: 15px
    }
  }
  .tenant-project-status-rejected {
    border:1px solid #FF363E !important;
    color: #FF363E !important;
  }
  .tenant-project-status-submitted {
    border:1px solid #F7AA51 !important;
    color: #F7AA51 !important;
  }
  &__th-summary {
    min-width: 200px;
  }
  &__th-updator {
    min-width: 150px;
  }
  &__th-status {
    min-width: 110px
  }
  &__th-date {
    min-width: 160px
  }
  &__th-remarks {
    display: flex;
    align-items: center;
    pre {
      margin-right: 5px;
      word-break: break-word;
    }
  }
}
.ecircular-modal {
  .modal-title {
    font-weight: bold;
  }
}
</style>
